import { FrostOverlay } from "@screener/uikit/components/frost-overlay";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { ScreenMode } from "./derive";

/**
 * Радиус круга оттаивания (§118 п. 1): половина ширины первой карточки в
 * области. Карточек нет — радиуса нет, и кругов тоже.
 */
export function thawRadiusOf(cardWidth: number | undefined): number {
  if (cardWidth === undefined || !(cardWidth > 0)) {
    return 0;
  }
  return cardWidth / 2;
}

/**
 * Область списка с инеем заморозки вида «Карточки» (§113). Кнопки заморозки в
 * других видах нет (§77 п. 1), поэтому и льда там нет. `frozen` приходит из
 * `useCardsFreeze`: все пути разморозки §77 п. 5 сбрасывают его же, и лёд
 * оттаивает сам. Вздрагивает вся обёртка области, не раскладка внутри неё.
 * Под курсором мыши лёд оттаивает кругом в половину ширины карточки (§118).
 */
export function CardsFrostFrame({
  mode,
  frozen,
  children,
}: {
  mode: ScreenMode;
  frozen: boolean;
  children: ReactNode;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [thawRadius, setThawRadius] = useState(0);

  // Карточки сетки одной ширины и следуют за шириной области; при смене
  // `frozen` их могло стать больше или меньше — меряем ещё раз.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `frozen` — повод перемерить, а не вход измерения
  useEffect(() => {
    const frame = frameRef.current;
    if (mode !== "cards" || frame === null) {
      setThawRadius(0);
      return;
    }
    // Ширину карточки задаёт её собственный `style.width` (use-card-layout), он
    // применяется после колбэка наблюдателя обёртки, поэтому радиус берётся из
    // наблюдателя самой карточки. Это же ловит полосу прокрутки.
    let watched: Element | null = null;
    const cardObserver = new ResizeObserver((entries) => {
      const entry = entries.at(-1);
      setThawRadius(thawRadiusOf(entry?.target.getBoundingClientRect().width));
    });
    // Обёртка менялась или карточки сменились: ищем первую карточку заново.
    // Окно карточек (DEN-104) снимает карточки с прокруткой, так что
    // наблюдаемая может уйти из DOM и без смены размера обёртки — состав
    // детей тоже повод искать заново.
    const watch = (): void => {
      const card = frame.querySelector("article");
      if (card === watched) {
        return;
      }
      cardObserver.disconnect();
      watched = card;
      if (card === null) {
        setThawRadius(0);
        return;
      }
      setThawRadius(thawRadiusOf(card.getBoundingClientRect().width));
      cardObserver.observe(card);
    };
    watch();
    const frameObserver = new ResizeObserver(watch);
    frameObserver.observe(frame);
    const childrenObserver = new MutationObserver(watch);
    childrenObserver.observe(frame, { childList: true, subtree: true });
    return () => {
      frameObserver.disconnect();
      childrenObserver.disconnect();
      cardObserver.disconnect();
    };
  }, [mode, frozen]);

  return (
    <div ref={frameRef} className="relative flex min-h-0 flex-1 flex-col">
      {children}
      {mode === "cards" ? (
        <FrostOverlay
          active={frozen}
          shakeTarget={frameRef}
          thawTarget={frameRef}
          thawRadius={thawRadius}
        />
      ) : null}
    </div>
  );
}
